<template>
  <section class="main-functions">
    <div class="functions-content">
      <div class="tab-navigation">
        <button 
          v-for="(tab, index) in tabs" 
          :key="index"
          :class="['tab-button', { active: selectedTab === index }]"
          @click="selectedTab = index"
        >
          {{ tab.title }}
        </button>
        <div class="tab-indicator" :style="{ left: `${(selectedTab * 100) / 3}%` }"></div>
      </div>
      <div class="tab-content">
        <div class="content-wrapper" :class="{ active: selectedTab === index }" v-for="(tab, index) in tabs" :key="index">
          <div class="text-content">
            <h3 class="content-title">{{ tab.title }}</h3>
            <p class="content-detail">{{ tab.detail }}</p>
          </div>
          <div class="image-content">
            <img :src="tab.image" :alt="tab.title" class="content-image">
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import networkBg from '@/assets/14039.jpg'
import meetingImg from '@/assets/AI_Assistant.png'
import digitalImg from '@/assets/digital.jpg'

const selectedTab = ref(0)

// 파티클 스타일은 AnimatedBackground 컴포넌트에서 처리

const tabs = [
  {
    title: "실시간 다국어 번역 강의",
    detail: `AITRA는 강의 내용을 사용자가 희망하는 200개 이상의 언어로 실시간 번역하여 제공합니다. The spirit is understood, not spoken 이라는 말이 있습니다. AITRA는 "Sense Beyond, Sprit Heard"을 슬로건으로 하며, 있는 그대로의 통번역이 아니라 업무 용어와 관계를 고려하여 상황 및 맥락을 잘 전달합니다. 따라서 강의 중 언어 장벽 없이 모든 학생이 자신의 모국어로 강의를 수강할 수 있습니다. 이를 통해 글로벌 학생들이 언어에 구애받지 않고 동일한 품질의 교육을 받을 수 있는 환경을 제공합니다. AITRA는 이해관계자들에게 Zoom 및 Google Translator에서는 경험하실 수 없는, 우수한 정확도와 성능을 차별화된 가치로 제공합니다.`,
    image: networkBg
  },
  {
    title: "AI 강의 지원 시스템",
    detail: "RAG(Retrieval-Augmented Generation) 기술을 활용한 AI 어시스턴트가 강의를 지원합니다. 교수자가 강의 자료(PDF, DOC 등)를 업로드할 경우, AI가 자동으로 핵심 개념과 전문 용어를 추출하여 맥락을 이해합니다. 이후 강의 중 학생이나 교수자의 질문이 있을 때, AI가 강의 자료에서 관련 정보를 실시간으로 검색하여 상황에 맞는 답변을 제공합니다. 이를 통해 학생들은 강의 내용을 더 깊이 이해할 수 있고, 교수자는 보조 자료를 즉시 참조할 수 있어 강의 효율성이 극대화됩니다.",
    image: meetingImg
  },
  {
    title: "자동 강의록 및 관리",
    detail: "강의가 종료되면 AI가 전체 강의 내용을 분석하여 자동으로 강의록을 생성합니다. 강의 제목, 핵심 키워드, 주요 학습 내용, 과제 및 복습 포인트가 포함된 구조화된 보고서를 표준 웹 형식으로 제공합니다. 학생들은 강의록을 통해 놓친 내용을 복습하고, 교수자는 강의 품질을 분석할 수 있습니다. 또한 AI 비서를 통해 강의록과 강의 자료에 대한 질의응답이 가능하여, 강의 후에도 지속적인 학습 지원이 이루어집니다. 모든 데이터는 안전하게 저장되어 학기 내내 활용할 수 있습니다.",
    image: digitalImg
  }
]
</script>

<style scoped>
.main-functions {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 4rem 2rem;
  width: 100%;
  overflow: hidden;
  background: transparent; /* 공통 배경 사용 */
}

.functions-content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

/* 배경 관련 CSS는 AnimatedBackground 컴포넌트에서 처리 */

.tab-navigation {
  display: flex;
  position: relative;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 2rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 0.5rem;
  padding: 0.5rem;
}

.tab-button {
  flex: 1;
  padding: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  text-align: center;
  border-radius: 0.5rem;
}

.tab-button:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.tab-button.active {
  color: white;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.2);
}

.tab-indicator {
  position: absolute;
  bottom: -2px;
  width: calc(100% / 3);
  height: 3px;
  background: linear-gradient(90deg, #fbbf24, #f59e0b); /* Yellow gradient for better visibility */
  transition: left 0.3s ease;
  border-radius: 2px 2px 0 0;
}

.tab-content {
  position: relative;
  min-height: 400px;
}

.content-wrapper {
  display: none;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.3s ease;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
}

.content-wrapper.active {
  display: grid;
  opacity: 1;
  transform: translateY(0);
}

.text-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.content-title {
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.content-detail {
  font-size: 1rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.image-content {
  width: 100%;
  height: 100%;
  min-height: 300px;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.1);
}

.content-wrapper.active .image-content {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.3);
}

.content-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 768px) {
  .main-functions {
    padding: 2rem 1rem;
  }

  .tab-button {
    font-size: 0.9rem;
    padding: 0.75rem;
  }

  .content-wrapper {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .content-title {
    font-size: 1.5rem;
  }

  .content-detail {
    font-size: 0.9rem;
  }

  .image-content {
    min-height: 200px;
  }
}
</style> 